import React, { useContext, useEffect, useState } from "react";
import { Platform } from "react-native";
import { useTheme } from "@emotion/react";
import {
    createStackNavigator,
    CardStyleInterpolators,
} from "@react-navigation/stack";
import { keyBy } from "lodash";
import ResourceCategory from "../ResourceCategory";
import ResourcesNearMe from "../ResourcesNearMe";
import DrawerButton from "../../components/DrawerButton";
import MyResourcesCtaSnippets from "./components/MyResourcesCtaSnippets";
import NearMeButton from "./components/NearMeButton";
import CategoryListItem from "../../components/CategoryListItem";
import { cardStyleInterpolatorSlide } from "../../utils/navigation";
import { FurnitureContext } from "../../utils/furniture";
import { useExternalState } from "../../utils/me";
import { getResourceOrder } from "../../utils/contentful";
import { useNearbyResources } from "../../utils/resources";
import {
    StackContainer,
    Container,
    Background,
    Title,
    Scroll,
    ResourceCategoryList,
} from "./styles";
import { AppTheme } from "myplan/utils/theme";

const Stack = createStackNavigator();

const Navigator = () => {
    const {
        backgroundPattern,
        color: { backgroundAccent },
    }: AppTheme = useTheme() as AppTheme;

    return (
        <StackContainer>
            <Background source={backgroundPattern} />
            <DrawerButton />
            <Stack.Navigator
                initialRouteName="MyResources"
                screenOptions={{
                    cardStyle: { backgroundColor: "transparent" },
                    presentation: "modal",
                    headerShown: false,
                    animationEnabled: true,
                    // @ts-ignore
                    cardStyleInterpolator: cardStyleInterpolatorSlide,
                }}>
                <Stack.Screen name="MyResources" component={MyResources} />
                <Stack.Screen
                    name="ResourceCategory"
                    component={ResourceCategory}
                />
                <Stack.Screen
                    name="ResourcesNearMe"
                    component={ResourcesNearMe}
                    options={{
                        cardStyleInterpolator:
                            Platform.OS === "android"
                                ? CardStyleInterpolators.forFadeFromBottomAndroid
                                : CardStyleInterpolators.forVerticalIOS,
                    }}
                />
                <Stack.Screen
                    name="ResourceCategoryNearMe"
                    component={ResourceCategory}
                    options={{
                        cardStyle: {
                            backgroundColor: backgroundAccent,
                        },
                        cardStyleInterpolator:
                            CardStyleInterpolators.forHorizontalIOS,
                    }}
                />
            </Stack.Navigator>
        </StackContainer>
    );
};

const MyResources = ({ navigation }) => {
    const [contentStatesMap, setContentsStateMap] = useState(null);
    const [resourceCategoryOrder, setResourceCategoryOrder] = useState(null);
    const [, setResourceOrder] = useState(null);
    const {
        myResourcesTitle,
        myResourcesCtaSnippets,
        resourceCategoryIndex,
        strings: { resourceLabel, resourcesLabel },
    } = useContext(FurnitureContext);
    const {
        contentStates,
        logicStates,
        api: { markContentAsRead },
    } = useExternalState();
    const [nearbyResourcesTitle, , , , , isNearbyResourcesEnabled] =
        useNearbyResources();

    useEffect(() => {
        if (logicStates && contentStates) {
            // if a content id appears in the content state map, it has been access or is Not New
            const _contentStatesMap = keyBy(contentStates, "contentId");
            setContentsStateMap(_contentStatesMap);

            const resourceIndex = resourceCategoryIndex.reduce(
                (acc, resourceCategory) =>
                    acc.concat(
                        resourceCategory.resources.map(strategy => ({
                            ...strategy,
                            _meta: {
                                resourceCategoryId: resourceCategory._id,
                                isNew: !_contentStatesMap[strategy._id],
                            },
                        })),
                    ),
                [],
            );
            const _resourceOrder = getResourceOrder(logicStates, resourceIndex);
            const availableResourceCategories = resourceCategoryIndex.reduce(
                (acc, resourceCategory) => {
                    const matches = resourceCategory.resources
                        .filter(resource =>
                            _resourceOrder.available.find(
                                _resource => _resource._id === resource._id,
                            ),
                        )
                        .map(resource => {
                            return {
                                ...resource,
                                _meta: {
                                    resourceCategoryId: resource._id,
                                    isNew: !_contentStatesMap[resource._id],
                                },
                            };
                        });

                    if (matches.length > 0) {
                        const hasNew = !!matches.find(m => m?._meta?.isNew);
                        const _resourceCategory = {
                            ...resourceCategory,
                            _meta: {
                                hasNew,
                            },
                            resources: matches,
                        };

                        acc.push(_resourceCategory);
                    }

                    return acc;
                },
                [],
            );

            const _resourceCategoryOrder = {
                all: resourceCategoryIndex,
                available: availableResourceCategories,
            };

            setResourceOrder(_resourceOrder);
            setResourceCategoryOrder(_resourceCategoryOrder);
        }

        // show and hide resources based upon the state of quizzes
    }, [contentStates, logicStates]);

    return (
        <Container>
            <Scroll showsVerticalScrollIndicator={false}>
                <Title type="h3">{myResourcesTitle}</Title>
                {myResourcesCtaSnippets && (
                    <MyResourcesCtaSnippets snippets={myResourcesCtaSnippets} />
                )}
                <ResourceCategoryList>
                    {resourceCategoryOrder?.available.map(resourceCategory => {
                        return (
                            <CategoryListItem
                                key={resourceCategory._id}
                                item={resourceCategory}
                                onPress={() => {
                                    markContentAsRead(resourceCategory._id);
                                    navigation.push("ResourceCategory", {
                                        resourceCategoryId:
                                            resourceCategory._id,
                                        data: resourceCategory,
                                    });
                                }}
                                count={resourceCategory.resources.length}
                                singular={resourceLabel}
                                plural={resourcesLabel}
                                icon={resourceCategory.icon}
                                payload={{ resourceCategory }}
                                hasNew={
                                    !contentStatesMap?.[resourceCategory._id]
                                }
                            />
                        );
                    })}
                </ResourceCategoryList>
            </Scroll>
            {isNearbyResourcesEnabled() ? (
                <NearMeButton
                    onPress={() => navigation.push("ResourcesNearMe")}>
                    {nearbyResourcesTitle}
                </NearMeButton>
            ) : null}
        </Container>
    );
};
export default Navigator;
